import { isGeomKind, type BoardObjectV2 } from "@bezpart-board/shared";
import { normalizeRuns, objectRuns } from "./textRuns";

const STYLE_KEYS = [
  "stroke",
  "strokeWidth",
  "strokeOpacity",
  "strokeLinecap",
  "strokeLinejoin",
  "strokeDash",
  "fill",
  "fillOpacity",
  "color",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textDecoration",
  "textAlign",
  "lineHeight",
  "backgroundColor",
  "borderColor",
  "borderWidth",
  "markerEnd",
] as const;

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function positiveSize(value: unknown, fallback = 1): number {
  const n = finiteNumber(value);
  if (n !== null && n > 0) return Math.min(n, 100_000);
  return fallback;
}

function sanitizeStyle(style: unknown): Record<string, unknown> | undefined {
  if (!style || typeof style !== "object") return undefined;
  const src = style as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of STYLE_KEYS) {
    if (src[key] !== undefined && src[key] !== null) out[key] = src[key];
  }
  return Object.keys(out).length ? out : undefined;
}

function sanitizePoints(raw: unknown): [number, number][] | null {
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const points: [number, number][] = [];
  for (const p of raw) {
    if (!Array.isArray(p) || p.length < 2) continue;
    const x = finiteNumber(p[0]);
    const y = finiteNumber(p[1]);
    if (x === null || y === null) continue;
    points.push([x, y]);
    if (points.length >= 8192) break;
  }
  return points.length >= 2 ? points : null;
}

function stripNulls<T extends Record<string, unknown>>(obj: T): T {
  const out = { ...obj };
  for (const key of Object.keys(out)) {
    if (out[key] === null || out[key] === undefined) delete out[key];
  }
  return out;
}

/**
 * Приводит объект к форме, которую принимает API (strict Zod).
 * Возвращает null, если объект нельзя сохранить.
 */
export function sanitizeObjectForOps(raw: BoardObjectV2): BoardObjectV2 | null {
  if (!raw || typeof raw !== "object" || typeof raw.id !== "string" || !raw.type) {
    return null;
  }

  const base: Record<string, unknown> = {
    id: raw.id,
    type: raw.type,
    x: finiteNumber(raw.x) ?? 0,
    y: finiteNumber(raw.y) ?? 0,
  };

  const rotation = finiteNumber(raw.rotation);
  if (rotation !== null) base.rotation = rotation;

  if (typeof raw.zIndex === "string") base.zIndex = raw.zIndex;
  else if (typeof raw.zIndex === "number" && Number.isFinite(raw.zIndex)) {
    base.zIndex = String(raw.zIndex);
  }

  if (typeof raw.locked === "boolean") base.locked = raw.locked;

  const opacity = finiteNumber(raw.opacity);
  if (opacity !== null) base.opacity = Math.min(1, Math.max(0, opacity));

  const style = sanitizeStyle(raw.style);
  if (style) base.style = style;

  if (typeof raw.documentId === "string") base.documentId = raw.documentId;
  const docPage = finiteNumber(raw.documentPage);
  if (docPage !== null) base.documentPage = Math.round(docPage);
  if (typeof raw.frameId === "string") base.frameId = raw.frameId;

  if (raw.meta && typeof raw.meta === "object") {
    base.meta = { ...(raw.meta as Record<string, unknown>) };
  }

  const type = raw.type;
  const sized = {
    ...base,
    w: positiveSize((raw as { w?: number }).w),
    h: positiveSize((raw as { h?: number }).h),
  };

  if (type === "text" || type === "sticky") {
    const text = (raw as { text?: unknown }).text;
    if (typeof text === "string") sized.text = text.slice(0, 4096);
    // Оформление кусками (часть текста жирная/цветная) — нормализуем и режем
    // по лимитам схемы, иначе сервер отклонит весь объект.
    const runs = objectRuns(raw as never);
    if (runs) (sized as Record<string, unknown>).runs = normalizeRuns(runs);
    return stripNulls(sized) as BoardObjectV2;
  }

  if (
    type === "rect" ||
    type === "ellipse" ||
    type === "triangle" ||
    type === "diamond" ||
    type === "frame" ||
    type === "geom"
  ) {
    if (type === "rect") {
      const cr = finiteNumber((raw as { cornerRadius?: number }).cornerRadius);
      if (cr !== null) sized.cornerRadius = cr;
      const label = (raw as { label?: unknown }).label;
      if (typeof label === "string") sized.label = label.slice(0, 4096);
    }
    if (type === "geom") {
      const kind = (raw as { kind?: unknown }).kind;
      if (!isGeomKind(kind)) return null;
      sized.kind = kind;
    }
    if (type === "frame") {
      const title = (raw as { title?: unknown }).title;
      if (typeof title === "string") sized.title = title.slice(0, 256);
    } else if ((raw as { showCenter?: unknown }).showCenter === true) {
      // У рамки такого поля нет — её схема строгая и отклонит объект целиком.
      sized.showCenter = true;
    }
    return stripNulls(sized) as BoardObjectV2;
  }

  if (type === "star") {
    const sp = finiteNumber((raw as { starPoints?: number }).starPoints);
    if (sp !== null) sized.starPoints = Math.round(sp);
    if ((raw as { showCenter?: unknown }).showCenter === true) sized.showCenter = true;
    return stripNulls(sized) as BoardObjectV2;
  }

  if (type === "image" || type === "audio") {
    const assetId = (raw as { assetId?: unknown }).assetId;
    if (typeof assetId !== "string" || !assetId) return null;
    sized.assetId = assetId;
    if (type === "audio") {
      const title = (raw as { title?: unknown }).title;
      if (typeof title === "string") sized.title = title.slice(0, 256);
      const together = (raw as { listenTogether?: unknown }).listenTogether;
      if (typeof together === "boolean") sized.listenTogether = together;
      const playing = (raw as { audioPlaying?: unknown }).audioPlaying;
      if (typeof playing === "boolean") sized.audioPlaying = playing;
      const pos = finiteNumber((raw as { audioPosition?: unknown }).audioPosition);
      if (pos !== null) sized.audioPosition = Math.max(0, Math.min(86_400, pos));
      const started = (raw as { audioStartedAt?: unknown }).audioStartedAt;
      if (typeof started === "number" && Number.isFinite(started)) sized.audioStartedAt = started;
      else if (started === null) sized.audioStartedAt = null;
      const leader = (raw as { audioLeaderId?: unknown }).audioLeaderId;
      if (typeof leader === "string") sized.audioLeaderId = leader.slice(0, 64);
      else if (leader === null) sized.audioLeaderId = null;
    }
    return stripNulls(sized) as BoardObjectV2;
  }

  if (type === "path") {
    const points = sanitizePoints((raw as { points?: unknown }).points);
    if (!points) return null;
    sized.points = points;
    return stripNulls(sized) as BoardObjectV2;
  }

  if (type === "line" || type === "arrow") {
    const points = sanitizePoints((raw as { points?: unknown }).points);
    if (!points || points.length < 2) return null;
    sized.points = points.slice(0, 2);
    const bindings = (raw as { bindings?: unknown }).bindings;
    if (bindings && typeof bindings === "object") sized.bindings = bindings;
    return stripNulls(sized) as BoardObjectV2;
  }

  if (type === "pdf_document") {
    const pdf = raw as {
      sourceAssetId?: unknown;
      pageAssetIds?: unknown;
      pageCount?: unknown;
      currentPage?: unknown;
      displayMode?: unknown;
      allowViewerPageNav?: unknown;
      presentationLeaderId?: unknown;
      title?: unknown;
    };
    if (typeof pdf.sourceAssetId !== "string") return null;
    if (!Array.isArray(pdf.pageAssetIds) || !pdf.pageAssetIds.length) return null;
    sized.sourceAssetId = pdf.sourceAssetId;
    sized.pageAssetIds = pdf.pageAssetIds.filter((id) => typeof id === "string");
    sized.pageCount = Math.round(finiteNumber(pdf.pageCount) ?? sized.pageAssetIds.length);
    sized.currentPage = Math.round(finiteNumber(pdf.currentPage) ?? 0);
    if (pdf.displayMode === "board" || pdf.displayMode === "presentation") {
      sized.displayMode = pdf.displayMode;
    }
    if (typeof pdf.allowViewerPageNav === "boolean") {
      sized.allowViewerPageNav = pdf.allowViewerPageNav;
    }
    if (typeof pdf.presentationLeaderId === "string") {
      sized.presentationLeaderId = pdf.presentationLeaderId.slice(0, 64);
    }
    if (typeof pdf.title === "string") sized.title = pdf.title.slice(0, 256);
    return stripNulls(sized) as BoardObjectV2;
  }

  if (type === "timer" || type === "calculator" || type === "code" || type === "sql") {
    // Виджеты копируем как есть, но без null-полей
    return stripNulls({ ...sized, ...(raw as object) }) as BoardObjectV2;
  }

  return null;
}

export function sanitizeObjectsForOps(objects: BoardObjectV2[]): {
  objects: BoardObjectV2[];
  skipped: number;
} {
  const out: BoardObjectV2[] = [];
  let skipped = 0;
  for (const obj of objects) {
    const clean = sanitizeObjectForOps(obj);
    if (clean) out.push(clean);
    else skipped += 1;
  }
  return { objects: out, skipped };
}
